import { AdminPageLayout } from "@/features/admin/components/AdminPageLayout";
import { DiaryManagementNavTabs } from "@/features/admin/components/DiaryManagementNavTabs";
import { useAdminStudies, type AdminStudyFilters } from "@/features/admin/studies/api/adminStudiesApi";
import { ADMIN_STUDIES_COLUMNS, AdminStudiesTable } from "@/features/admin/studies/components/AdminStudiesTable";
import {
    AdminStudiesToolbar,
    type AdminStudiesFilterValues,
} from "@/features/admin/studies/components/AdminStudiesToolbar";
import {
    ADMIN_TABLE_PAGE_SIZE,
    AdminColumnsDialog,
    AdminTablePagination,
    useDebouncedValue,
} from "@/features/admin/tables";
import { useNav } from "@/utils/nav";
import { ui } from "@patient-watch/ui";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";

const StudiesSearchZ = z.object({
    q: z.string().optional(),
    page: z.coerce.number().int().min(1).optional(),
    organisationId: z.string().optional(),
    createdBy: z.string().optional(),
    status: z.enum(["published", "draft"]).optional(),
});

export const Route = createFileRoute("/_app/admin/studies")({
    component: AdminStudiesPage,
    validateSearch: (search) => StudiesSearchZ.parse(search),
});

function AdminStudiesPage() {
    useNav([
        { label: "Admin", route: { to: "/admin" } },
        { label: "Studies", route: { to: "/admin/studies" } },
    ]);

    const search = Route.useSearch();
    const navigate = Route.useNavigate();

    const [searchText, setSearchText] = useState(search.q ?? "");
    const debouncedSearch = useDebouncedValue(searchText.trim(), 300);
    const [visibleKeys, setVisibleKeys] = useState(() =>
        ADMIN_STUDIES_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.key)
    );
    const page = search.page ?? 1;

    const filterValues: AdminStudiesFilterValues = useMemo(
        () => ({
            organisationId: search.organisationId ?? null,
            createdBy: search.createdBy ?? null,
            status: search.status ?? "all",
        }),
        [search.organisationId, search.createdBy, search.status]
    );

    useEffect(() => {
        if (debouncedSearch === (search.q ?? "")) return;
        void navigate({
            search: (prev) => ({ ...prev, q: debouncedSearch || undefined, page: 1 }),
            replace: true,
        });
    }, [debouncedSearch, navigate, search.q]);

    const filters: AdminStudyFilters = useMemo(
        () => ({
            q: debouncedSearch || undefined,
            organisationId: filterValues.organisationId ?? undefined,
            createdBy: filterValues.createdBy ?? undefined,
            isPublished: filterValues.status === "all" ? undefined : filterValues.status === "published",
            page,
            pageSize: ADMIN_TABLE_PAGE_SIZE,
        }),
        [debouncedSearch, filterValues, page]
    );

    const { data, isLoading, isFetching, error } = useAdminStudies(filters);

    return (
        <AdminPageLayout
            title="Studies"
            description="Every study across organisations, with the templates, questionnaires and invites in each."
        >
            <div className="space-y-4">
                <DiaryManagementNavTabs
                    actions={
                        <AdminStudiesToolbar
                            searchText={searchText}
                            onSearchTextChange={setSearchText}
                            filterValues={filterValues}
                            onApplyFilters={(values) =>
                                void navigate({
                                    search: (prev) => ({
                                        ...prev,
                                        page: 1,
                                        organisationId: values.organisationId ?? undefined,
                                        createdBy: values.createdBy ?? undefined,
                                        status: values.status === "all" ? undefined : values.status,
                                    }),
                                    replace: true,
                                })
                            }
                            isApplying={isFetching}
                        >
                            <AdminColumnsDialog
                                columns={ADMIN_STUDIES_COLUMNS}
                                visibleKeys={visibleKeys}
                                onSave={setVisibleKeys}
                            />
                        </AdminStudiesToolbar>
                    }
                />

                {error ? (
                    <div className="bg-red-50 p-4 border border-red-200 rounded-md text-red-800 text-sm">
                        Error loading studies: {error instanceof Error ? error.message : "Unknown error"}
                    </div>
                ) : isLoading ? (
                    <ui.Skeleton className="w-full h-64" />
                ) : (
                    <div className={isFetching ? "opacity-70 transition-opacity" : undefined}>
                        <AdminStudiesTable studies={data?.studies ?? []} visibleKeys={visibleKeys} />
                        <div className="mt-3">
                            <AdminTablePagination
                                page={page}
                                totalCount={data?.totalCount ?? 0}
                                pageSize={ADMIN_TABLE_PAGE_SIZE}
                                isFetching={isFetching}
                                onPageChange={(nextPage) =>
                                    void navigate({ search: (prev) => ({ ...prev, page: nextPage }), replace: true })
                                }
                            />
                        </div>
                    </div>
                )}
            </div>
        </AdminPageLayout>
    );
}
